import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Email as EmailIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
  TextSnippet as TemplateIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import apiClient from '../../utils/api';
import { MessageTemplate } from '../../types';

interface CreateChatRoomModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (chatRoom: any) => void;
}

interface CreateChatRoomForm {
  title: string;
  description: string;
  participantName: string;
  participantEmail: string;
  participantPhone: string;
  initialMessage: string;
  sendMethod: 'sms' | 'email' | 'both';
}

const CreateChatRoomModal: React.FC<CreateChatRoomModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [form, setForm] = useState<CreateChatRoomForm>({
    title: '',
    description: '',
    participantName: '',
    participantEmail: '',
    participantPhone: '',
    initialMessage: '',
    sendMethod: 'sms'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState<null | HTMLElement>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({ title: '', content: '', category: 'general' as string });
  const [savingTemplate, setSavingTemplate] = useState(false);

  const handleChange = (field: keyof CreateChatRoomForm) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // バリデーション
    if (!form.title.trim()) {
      setError('チャットルーム名は必須です');
      return;
    }
    const wantsSms = form.sendMethod === 'sms' || form.sendMethod === 'both';
    const wantsEmail = form.sendMethod === 'email' || form.sendMethod === 'both';

    if (!form.participantName.trim()) {
      setError('お客様名は必須です');
      return;
    }

    if (wantsEmail && !form.participantEmail.trim()) {
      setError('メール送信を選択した場合、メールアドレスは必須です');
      return;
    }

    if (wantsEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.participantEmail)) {
        setError('正しいメールアドレス形式で入力してください');
        return;
      }
    }

    if (wantsSms && !form.participantPhone.trim()) {
      setError('SMS送信を選択した場合、電話番号は必須です');
      return;
    }

    // 送信前の確認ダイアログ
    const confirmed = window.confirm(
      form.sendMethod === 'sms'
        ? `作成するとSMSが送信されます（電話番号: ${form.participantPhone || '未入力'}）。本当に作成しますか？`
        : form.sendMethod === 'both'
          ? `作成するとSMSとメールが送信されます。\n電話番号: ${form.participantPhone || '未入力'}\nメール: ${form.participantEmail || '未入力'}\n本当に作成しますか？`
          : `作成すると記載したメールアドレス（${form.participantEmail}）にメールが送信されます。本当に作成しますか？`
    );
    if (!confirmed) {
      return; // 入力内容は保持したまま
    }

    setLoading(true);
    setError(null);

    try {
      // APIクライアント経由で作成（作成者情報は内部で自動付与）
      const payload = {
        title: form.title.trim(),
        description: form.description,
        participantName: form.participantName,
        participantEmail: form.participantEmail.trim() || undefined,
        participantPhone: form.participantPhone.trim() || undefined,
        initialMessage: form.initialMessage.trim() || undefined,
        sendMethod: form.sendMethod,
        autoCloseEnabled: undefined,
        autoCloseDays: undefined,
      } as any;
      const resp = await apiClient.createChatRoom(payload);
      const chatRoom = (resp as any)?.chatRoom || (resp as any)?.data;
      const emailSent = (resp as any)?.emailSent;
      const emailError = (resp as any)?.emailResponse?.error;

      if ((resp as any)?.success && chatRoom) {
        const emailStatus = emailSent
          ? '✅ 招待メールを送信しました'
          : emailError
            ? `⚠️ メール送信に失敗: ${emailError}`
            : '';

        setSuccess(`🎉 チャットルーム「${chatRoom.title}」が作成されました！${emailStatus ? `\n${emailStatus}` : ''}`);
        onSuccess(chatRoom);

        setTimeout(() => {
          handleReset();
          onClose();
        }, emailStatus ? 3500 : 2000);
      } else {
        setError((resp as any)?.message || 'チャットルーム作成に失敗しました');
      }
    } catch (err) {
      setError('チャットルーム作成中にエラーが発生しました');
      console.error('Create chat room error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm({
      title: '',
      description: '',
      participantName: '',
      participantEmail: '',
      participantPhone: '',
        initialMessage: '',
        sendMethod: 'sms'
    });
    setError(null);
    setSuccess(null);
    setLoading(false);
  };

  const handleClose = () => {
    if (!loading) {
      handleReset();
      onClose();
    }
  };

  const handleTemplateMenuOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setTemplateMenuAnchor(event.currentTarget);
    setLoadingTemplates(true);
    try {
      const response = await apiClient.getTemplates();
      if (response.success) {
        setTemplates(response.templates);
      }
    } catch (err) {
      console.error('テンプレート取得エラー:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateMenuClose = () => {
    setTemplateMenuAnchor(null);
  };

  const handleTemplateSelect = async (template: MessageTemplate) => {
    try {
      await apiClient.useTemplate(template.id);
    } catch (err) {
      console.error('テンプレート使用記録エラー:', err);
    }
    setForm(prev => ({ ...prev, initialMessage: template.content }));
    handleTemplateMenuClose();
  };

  const handleCreateTemplateOpen = () => {
    setTemplateForm({ title: '', content: '', category: 'general' });
    setTemplateDialogOpen(true);
    handleTemplateMenuClose();
  };

  const handleTemplateSave = async () => {
    if (!templateForm.title.trim() || !templateForm.content.trim()) return;
    setSavingTemplate(true);
    try {
      await apiClient.createTemplate(templateForm as any);
      setTemplateDialogOpen(false);
      setTemplateForm({ title: '', content: '', category: 'general' });
    } catch (err) {
      console.error('テンプレート作成エラー:', err);
    } finally {
      setSavingTemplate(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TitleIcon color="primary" />
          <Typography variant="h6">
            新規チャットルーム作成
          </Typography>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ py: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* チャットルーム情報 */}
            <Box>
              <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                📋 チャットルーム情報
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="タイトル 例）BiZiMo光 工事日調整"
                  value={form.title}
                  onChange={handleChange('title')}
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: <TitleIcon sx={{ mr: 1, color: 'action.active' }} />
                  }}
                  placeholder="例：BiZiMo光 工事日調整"
                />

                <TextField
                  fullWidth
                  label="説明・概要"
                  value={form.description}
                  onChange={handleChange('description')}
                  multiline
                  rows={2}
                  variant="outlined"
                  inputProps={{ maxLength: 40 }}
                  InputProps={{
                    startAdornment: <DescriptionIcon sx={{ mr: 1, color: 'action.active', alignSelf: 'flex-start', mt: 1 }} />
                  }}
                  placeholder="お問い合わせの概要や背景情報を入力してください"
                  helperText={`${form.description.length}/40文字`}
                />
              </Box>
            </Box>

            {/* お客様情報 */}
            <Box>
              <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                👤 お客様情報
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    fullWidth
                    label="お客様名"
                    value={form.participantName}
                    onChange={handleChange('participantName')}
                    required
                    variant="outlined"
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                    }}
                    placeholder="例：山田 太郎"
                  />

                  <TextField
                    fullWidth
                    label={form.sendMethod === 'sms' ? 'メールアドレス（任意）' : 'メールアドレス'}
                    type="email"
                    value={form.participantEmail}
                    onChange={handleChange('participantEmail')}
                    required={form.sendMethod === 'email' || form.sendMethod === 'both'}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
                    }}
                    placeholder="例：yamada@example.com"
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                  <TextField
                    fullWidth
                    label={form.sendMethod === 'email' ? '電話番号（任意）' : '電話番号'}
                    value={form.participantPhone}
                    onChange={handleChange('participantPhone')}
                    required={form.sendMethod === 'sms' || form.sendMethod === 'both'}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />
                    }}
                    placeholder="例：090-1234-5678"
                  />
                </Box>
              </Box>
            </Box>

            {/* 招待方法 */}
            <Box>
              <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                ✉️ 招待方法
              </Typography>
              <FormControl component="fieldset">
                <FormLabel component="legend">送信手段を選択</FormLabel>
                <RadioGroup
                  row
                  value={form.sendMethod}
                  onChange={(_, value) => {
                    setForm(prev => ({ ...prev, sendMethod: value as 'sms' | 'email' | 'both' }));
                    setError(null);
                  }}
                >
                  <FormControlLabel value="sms" control={<Radio />} label="SMS" />
                  <FormControlLabel value="email" control={<Radio />} label="メール" />
                  <FormControlLabel value="both" control={<Radio />} label="SMS + メール" />
                </RadioGroup>
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  SMSを選ぶ場合は電話番号が必須、メールを選ぶ場合はメールアドレスが必須です。
                </Typography>
              </FormControl>
            </Box>

            {/* 初期メッセージ */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold' }}>
                  💬 初期メッセージ（任意）
                </Typography>
                <Tooltip title="テンプレートから挿入">
                  <IconButton
                    size="small"
                    onClick={handleTemplateMenuOpen}
                    sx={{ color: 'primary.main' }}
                  >
                    <TemplateIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <TextField
                fullWidth
                label="ハイホーからの初期メッセージ"
                value={form.initialMessage}
                onChange={handleChange('initialMessage')}
                multiline
                rows={3}
                variant="outlined"
                placeholder="お客様へのご挨拶や最初のご案内を入力してください"
                helperText="入力した場合、ハイホーからのメッセージとしてチャットに表示されます。テンプレートボタンから定型文を挿入できます。"
              />
            </Box>

            {/* テンプレート選択メニュー */}
            <Menu
              anchorEl={templateMenuAnchor}
              open={Boolean(templateMenuAnchor)}
              onClose={handleTemplateMenuClose}
              PaperProps={{
                style: { maxHeight: 450, width: 380 },
              }}
            >
              <MenuItem onClick={handleCreateTemplateOpen}>
                <AddIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">新規テンプレート作成</Typography>
              </MenuItem>
              <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />
              {loadingTemplates ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  読み込み中...
                </MenuItem>
              ) : templates.length === 0 ? (
                <MenuItem disabled>テンプレートがありません</MenuItem>
              ) : (
                templates.map((template) => (
                  <MenuItem
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      py: 1.5,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {template.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.8rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {template.content}
                    </Typography>
                  </MenuItem>
                ))
              )}
            </Menu>

            {/* テンプレート新規作成ダイアログ */}
            <Dialog
              open={templateDialogOpen}
              onClose={() => setTemplateDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>テンプレート新規作成</DialogTitle>
              <DialogContent>
                <TextField
                  label="タイトル"
                  fullWidth
                  value={templateForm.title}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, title: e.target.value }))}
                  sx={{ mt: 1, mb: 2 }}
                  placeholder="例：初回ご挨拶"
                />
                <TextField
                  label="テンプレート内容"
                  fullWidth
                  multiline
                  rows={6}
                  value={templateForm.content}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="テンプレートの本文を入力してください"
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setTemplateDialogOpen(false)}>キャンセル</Button>
                <Button
                  variant="contained"
                  onClick={handleTemplateSave}
                  disabled={savingTemplate || !templateForm.title.trim() || !templateForm.content.trim()}
                >
                  {savingTemplate ? <CircularProgress size={20} /> : '保存'}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
          >
            キャンセル
          </Button>
          <Button
            type="submit"
            disabled={loading}
            variant="contained"
            sx={{ minWidth: 120 }}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              '作成'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateChatRoomModal;
